import Button from '@/shared/Button'
import FormItem from '@/shared/FormCreator/FormItem'
import AddIcon from '@mui/icons-material/Add'
// import debounce from '@mui/material/utils/debounce'
import AddCircle from '@mui/icons-material/AddCircle'
import {
  Box,
  Card,
  CardActionArea,
  Container,
  Grid,
  Stack,
  Typography,
  Tooltip,
  debounce,
} from '@mui/material'

import isEmpty from 'lodash/isEmpty'
import unionBy from 'lodash/unionBy'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect, useSelector } from 'react-redux'

import FormSection from '@/components/FormSection'
import PhishCampaignCreateGroupItem from '@/components/PhishCampaignCreateGroupItem'
import EmailTemplatePreviewDialog from '@/components/EmailTemplatePreviewDialog'
import CreateTestCompanyDialog from '@/components/TestCompany/CreateTestCompanyDialog'
import LoadingDialog from '@/components/LoadingDialog/LoadingDialog'
import usePermissions from '@/hooks/usePermissions'
import { v4 as uuidv4 } from 'uuid'

const mapStateToProps = ({ campaign, loading }) => ({
  options: campaign.createCampaignOptions,
  isTestCompanyLoading: loading['account/getTestCompany'],
})

const inputConfig = { inputBaseProps: { alignItems: 'center' } }

const initialList = [
  { key: 'campaign0', id: 0, sendingTimes: [{ id: uuidv4(), dateTime: new Date() }] },
]
const initialMultiList = [
  { key: 'campaign0', id: 0, sendingTimes: [{ id: uuidv4(), dateTime: new Date() }] },
  { key: 'campaign1', id: 1, sendingTimes: [{ id: uuidv4(), dateTime: new Date() }] },
]

const PhishCampaignEmailCreateFormGeneral = ({
  dispatch,
  form,
  onSubmit,
  onTestsubmit,
  isTestCompanyLoading,
  itemRefs,
  createList,
  setCreateList,
}) => {
  const { t } = useTranslation()

  const testCompanySearchInputRef = useRef()
  const imageFileRef = useRef()

  const dispatchLoading =
    useSelector(state => state.loading['phishCampaign/createPhishCampaign']) ?? false

  const [testCompanySearchInput, setTestCompanySearchInput] = useState('')
  const [testCompanySearchResults, setTestCompanySearchResults] = useState({})
  const [testCompanyPage, setTestCompanyPage] = useState(0)
  const [createTestCompanyDialogOpen, setCreateTestCompanyDialogOpen] = useState(false)

  // const [emailTemplatePreview, setEmailTemplatePreview] = useState()
  // const [landingPagePreview, setLandingPagePreview] = useState()

  const [previewEmailTemplateId, setPreviewEmailTemplateId] = useState()
  // const [showLandingPagePreview, setShowLandingPagePreview] = useState(false)
  const [testDisabled, setTestDisabled] = useState(true)

  const { data: testCompanySearchResultsData = [], paging: testCompanySearchResultsPaging } =
    testCompanySearchResults

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    // eslint-disable-next-line no-unused-vars
    formState: { errors },
  } = form

  const followUpAction = watch('followUpAction')
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    dispatch({
      type: 'account/getTestCompany',
      successCallback: (testCompanies) => {
        if (Array.isArray(testCompanies.data) && testCompanies.data.length > 0) {
          setCompanyId(testCompanies.data[0].companyId);
        }
      },
    })
  }, [dispatch])

  // fetch test company START
  const fetchTestCompany = useCallback(() => {
    dispatch({
      type: 'account/getTestCompany',
      payload: {
        pageIndex: testCompanyPage,
        pageSize: 100,
        searchName: testCompanySearchInputRef.current,
      },
      successCallback: response => {
        const { data: responseData = [], paging: responsePaging = {} } = response || {}
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.testcompanyId, label: d.name }))

        setTestCompanySearchResults(s => ({
          data: pageIndex > 0 ? unionBy(s.data || [], newData, 'id') : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, testCompanyPage])

  useEffect(() => {
    setCreateList(initialList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const updateSendingTimes = useCallback(
    (index, sendingTimes) => {
      setCreateList(prevList =>
        prevList.map(item => (item.id === index ? { ...item, sendingTimes } : item)),
      )
    },
    [setCreateList],
  )

  const loadMoreTestCompanyDebounce = debounce(() => {
    setTestCompanyPage(p => p + 1)
  }, 400)

  const fetchTestCompanyDebounce = useMemo(
    () =>
      debounce(() => {
        fetchTestCompany()
      }, 400),
    [fetchTestCompany],
  )

  useEffect(() => {
    fetchTestCompanyDebounce()
  }, [testCompanySearchInput, testCompanyPage, fetchTestCompanyDebounce])
  // fetch test company END

  return (
    <Container maxWidth={false}>
      <Card>
        <Box component="form">
          <FormSection title={t('general.basic_info')}>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <FormItem
                  label={t('general.campaign_name')}
                  defaultValue=""
                  name="name"
                  control={control}
                  inputConfig={{ ...inputConfig, inputProps: { maxLength: 30 } }}
                  errors={errors}
                />
              </Grid>
              <Grid item xs={10} sm={6} style={{ display: 'flex', alignItems: 'center' }}>
                <FormItem
                  label={t('general.select_test_company')}
                  name="testCompany"
                  type="autocomplete"
                  defaultValue=""
                  control={control}
                  errors={errors}
                  inputConfig={{
                    ...inputConfig,
                    onInputChange: (_, value, reason) => {
                      testCompanySearchInputRef.current = value

                      if (['input', 'clear'].includes(reason)) {
                        setTestCompanyPage(0)
                        setTestCompanySearchInput(value)
                      }
                    },
                    onFocus: () => {
                      fetchTestCompanyDebounce()
                    },
                    onLoadMore: () => {
                      const { hasNext } = testCompanySearchResultsPaging

                      if (!isTestCompanyLoading && hasNext) {
                        loadMoreTestCompanyDebounce()
                      }
                    },
                    options: testCompanySearchResultsData,
                    filterOptions: opts => opts,
                    isOptionEqualToValue: (option, value) => option.id === value.id,
                    renderOption: (renderProps, option) => (
                      <Box component="li" {...renderProps} key={option.id}>
                        {option.label}
                      </Box>
                    ),
                  }}
                />
                <Tooltip title={t('general.add_logo')} placement="top-start">
                  <AddIcon
                    sx={{ pl: 1 }}
                    onClick={() => {
                      setCreateTestCompanyDialogOpen(true)
                    }}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormItem
                  defaultValue={'0'}
                  label={t('general.select_send_count')}
                  name="sendCount"
                  type="radio"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    onChange: e => {
                      itemRefs.current = []
                      setCreateList(
                        e.target.value === '0' ? [...initialList] : [...initialMultiList],
                      )

                      if (e.target.value === '1') {
                        setTestDisabled(true)
                      }
                    },
                    options: [
                      { value: '0', label: t('general.one_group') },
                      { value: '1', label: t('general.multiple_group') },
                    ],
                  }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormItem
                  defaultValue={'S'}
                  label={t('general.campaign_type')}
                  name="campaignType"
                  type="radio"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    options: [
                      { value: 'S', label: t('general.simulate') },
                      { value: 'B', label: t('general.BEC'), disabled: true },
                    ],
                  }}
                  errors={errors}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormItem
                  defaultValue={'main'}
                  label={t('general.campaign_run_type')}
                  name="campaignRunType"
                  type="radio"
                  control={control}
                  inputConfig={{
                    ...inputConfig,
                    options: [
                      { value: 'main', label: t('general.run_type_main') },
                      { value: 'pre', label: t('general.run_type_pre') },
                      ...(companyId === 1 ? [{ value: 'test', label: t('general.run_type_test') }] : []),
                    ],
                  }}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title={t('general.select_send_group')} contentSx={{ p: 0 }}>
            {createList.map((obj, i) => (
              <div key={obj.key}>
                <PhishCampaignCreateGroupItem
                  ref={ref => {
                    if (ref) {
                      itemRefs.current[i] = ref
                      const { watch: watchChildForm } = ref?.form || {}

                      if (createList?.length === 1) {
                        watchChildForm?.(childFormValue => {
                          const {
                            phishType,
                            emailTemplateLabel,
                            emailTemplate,
                            landingType,
                            landingPage,
                          } = childFormValue || {}

                          const isPhishRandom = phishType === 'R' || phishType === 'T'
                          const isLandingDefault = landingType === 'default'

                          setTestDisabled(
                            (isPhishRandom
                              ? isEmpty(emailTemplateLabel)
                              : isEmpty(emailTemplate)) ||
                              (isLandingDefault ? false : isEmpty(landingPage)),
                          )
                        })
                      }
                    }
                  }}
                  index={i}
                  id={obj.id}
                  updateSendingTimes={updateSendingTimes}
                  onDelete={() => {
                    itemRefs.current?.splice?.(i, 1)
                    setCreateList([...createList].filter(o => o.id !== obj.id))
                  }}
                  multi={createList.length > 1}
                  setPreviewEmailTemplateId={setPreviewEmailTemplateId}
                  sendingTimes={obj.sendingTimes}
                />
              </div>
            ))}
            {createList.length > 1 && createList.length < 10 && (
              <CardActionArea
                onClick={() => {
                  const latestId = createList[createList.length - 1].id + 1
                  setCreateList([
                    ...createList,
                    {
                      key: `campaign${latestId}`,
                      id: latestId,
                      sendingTimes: [{ id: uuidv4(), dateTime: new Date() }],
                    },
                  ])
                }}
              >
                <Stack
                  alignItems={'center'}
                  justifyContent={'center'}
                  p={5}
                  spacing={1}
                  border={theme => `1px dashed ${theme.palette.secondary_80.main}`}
                >
                  <AddCircle sx={{ height: 50, width: 50 }} />
                  <Typography variant="h5" fontWeight={'bold'}>
                    {t('general.add_send_group')}
                  </Typography>
                </Stack>
              </CardActionArea>
            )}
          </FormSection>

          <FormSection title={t('general.followup_settings')}>
            {/* <Typography>{t('general.followup_notify_setting')}</Typography>
            <Typography variant="caption" color={theme => theme.palette.other.grey[60].main}>
              {t('messages.followup_notify')}
            </Typography> */}

            <Stack mt={2} spacing={1}>
              {/* <FormItem
                defaultValue={'1'}
                name="followUpAction"
                type="radio"
                control={control}
                inputConfig={{
                  ...inputConfig,
                  options: [
                    { value: 1, label: t('general.close') },
                    { value: 2, label: t('general.send_general_reply') },
                    // { value: 3, label: t('general.send_training_course') },
                  ],
                }}
                errors={errors}
              /> */}

              {followUpAction !== '1' && (
                <Stack direction="row" spacing={2}>
                  <Box width={followUpAction === '2' ? 115 : 285} />
                  {/* <Stack>
                    <Typography>{t('general.user_behaviour')}</Typography>
                    <FormItem
                      label={t('general.click_link')}
                      defaultValue={false}
                      name="linkClicked"
                      type="checkbox"
                      control={control}
                      inputConfig={inputConfig}
                      errors={errors}
                    />
                    <FormItem
                      label={t('general.enter_data')}
                      defaultValue={false}
                      name="dataEntered"
                      type="checkbox"
                      control={control}
                      inputConfig={inputConfig}
                      errors={errors}
                    />
                    <FormItem
                      label={t('general.open_attachment')}
                      defaultValue={false}
                      name="attachmentOpen"
                      type="checkbox"
                      control={control}
                      inputConfig={inputConfig}
                      errors={errors}
                    />
                  </Stack> */}

                  {/* <Stack>
                    <Typography>{t('general.select_send_template')}</Typography>
                  </Stack> */}
                </Stack>
              )}
            </Stack>
          </FormSection>

          <Stack direction="row" justifyContent="flex-end" p={2} spacing={2}>
            {/* <Button color={'btn_positive'} sx={{ float: 'right', m: 2 }} type="submit">
              {t(`general.confirm_send_schedule`)}
            </Button> */}
            <Button
              disabled={testDisabled || dispatchLoading}
              onClick={async () => {
                handleSubmit(onTestsubmit, () => {
                  trigger?.()
                })()
              }}
            >
              {t(`general.send_test_campaign`)}
            </Button>
            <Button
              disabled={dispatchLoading}
              onClick={async () => {
                const refs = itemRefs.current

                // eslint-disable-next-line no-restricted-syntax
                for (const r of refs) {
                  // eslint-disable-next-line no-await-in-loop
                  await r.form.trigger?.()
                }

                const hasError =
                  itemRefs.current?.filter?.(r => !r.form.formState.isValid).length > 0

                if (hasError) {
                  return
                }

                if (followUpAction === '3') {
                  handleSubmit(() => {
                    setValue('checkFollowUp', true)
                  })()
                } else {
                  handleSubmit(onSubmit, () => {
                    trigger?.()
                  })()
                }
              }}
            >
              {t(`general.${followUpAction === '3' ? 'continue_edit' : 'create_campaign'}`)}
            </Button>
          </Stack>
        </Box>
      </Card>

      <EmailTemplatePreviewDialog
        open={!!previewEmailTemplateId}
        onClose={() => {
          setPreviewEmailTemplateId()
        }}
        emailTempId={previewEmailTemplateId}
      />

      {/* add test company dialog */}
      <CreateTestCompanyDialog
        open={createTestCompanyDialogOpen}
        onClose={() => {
          setCreateTestCompanyDialogOpen(false)
        }}
        onFileChange={file => {
          imageFileRef.current = file
        }}
      />
      <LoadingDialog type={'phishCampaign/createPhishCampaign'} />
    </Container>
  )
}

export default connect(mapStateToProps)(PhishCampaignEmailCreateFormGeneral)
